import { useEffect, useRef, useState } from "react";
import { statsData } from "@/mocks/stats";

function AnimatedCounter({ target, duration = 2000 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let start = 0;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [isVisible, target, duration]);

  return (
    <div ref={ref} className="text-3xl md:text-4xl font-light text-[#5cb85c]">
      {count.toLocaleString()}
    </div>
  );
}

export default function StatsSection() {
  return (
    <section className="py-12 md:py-16 bg-[#0d1117]">
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto text-center">
        <div className="mb-10">
          <h3 className="text-xl md:text-2xl font-semibold text-gray-200 mb-2">
            We are back, better and repackaged in order to serve you better!
          </h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {statsData.map((stat) => (
            <div key={stat.label} className="flex flex-col items-center">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center border border-gray-300 rounded mb-4">
                <i className={`fas ${stat.icon} text-xl md:text-2xl text-gray-500`} />
              </div>
              <AnimatedCounter target={stat.number} />
              <h3 className="text-sm md:text-base font-medium text-gray-400 mt-2">
                {stat.label}
              </h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}